import { cursorPointer } from '@/component/NaviTop/style.css'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Spinner from 'react-bootstrap/Spinner'
import { CheckLineIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { Company, SetBookmarkPayload } from './data'
import { fetchBookmarkedCompanies, setBookmark } from './data'
import Badge from 'react-bootstrap/Badge'
import Stack from 'react-bootstrap/Stack'

export default function BookmarkedCompany() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    data: companies,
    isLoading: loadingCompanies,
    isError: companiesError,
  } = useQuery<Company[]>({
    queryKey: ['company', 'bookmarked'],
    queryFn: fetchBookmarkedCompanies,
    placeholderData: () => {
      return queryClient.getQueryData<Company[]>(['company'])?.filter((c) => c.bookmarked) || []
    },
  })

  const bookmarkMutation = useMutation({
    mutationFn: setBookmark,
    onMutate: ({ id, action }: SetBookmarkPayload) => {
      queryClient.setQueryData(['company', 'bookmarked'], (companies: Company[]) =>
        companies.map((company) =>
          company.id === id ? { ...company, bookmarked: action === 'ADD' } : company
        )
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'bookmarked'] })
    },
  })

  useEffect(() => {
    if (companies) {
      companies.forEach((company) => {
        queryClient.setQueryData(['company', company.id], company)
      })
    }
  }, [companies, queryClient])

  return (
    <>
      {loadingCompanies ? (
        <Spinner />
      ) : companiesError ? (
        <Alert variant="danger">error</Alert>
      ) : (
        companies?.map((company) => (
          <Card
            key={company.id}
            className={`my-4 ${cursorPointer}`}
            onClick={() => navigate(`/company/${company.id}/view`)}
          >
            <Card.Body>
              <Card.Title className="d-flex justify-content-between">{company.name}</Card.Title>
              <div>
                <ul>
                  {company.summary?.map((summaryItem, idx) => (
                    <li key={idx} className="text-muted">
                      {summaryItem}
                    </li>
                  ))}
                </ul>
              </div>
              <Stack direction="horizontal" gap={2} className="mb-2 flex-wrap">
                {company.industries?.map((industry) => (
                  <Badge bg="secondary" key={industry.id}>
                    {industry.name}
                  </Badge>
                ))}
              </Stack>
              <Card.Footer className="d-flex justify-content-center">
                <Button
                  variant={company.bookmarked ? 'primary' : 'outline-secondary'}
                  className="d-flex align-items-center"
                  onClick={(e) => {
                    e.stopPropagation()
                    bookmarkMutation.mutate({
                      id: company.id,
                      action: company.bookmarked ? 'REMOVE' : 'ADD',
                    })
                  }}
                >
                  <CheckLineIcon className="me-1" />
                  {company.bookmarked ? 'チェック済み' : '興味あり'}
                </Button>
              </Card.Footer>
            </Card.Body>
          </Card>
        ))
      )}
    </>
  )
}
